const STATES = ['', '00', '12', '18', '24'];
const OPEN_DURATION = 1100;
const OPEN_DELAY = 400;
const OPEN_EASE_X2 = 0.199124;
const MARKER_FADE_DURATION = 200;
const KEY_STEP_DURATION = 0.4;
const DIAL_CX = 280;
const DIAL_CY = 244;
const LABEL_GAP = 16;
const LOWER_LABEL_GAP = 12;
const LABEL_BOX_WIDTH = 120;
const LABEL_BOX_HEIGHT = 64;

// Invert the open-arc cubic-bezier(0, 0, 0.199124, 1) so a marker appears when the stroke reaches its slot.
function timeAtOpenStrokeProgress(progress) {
  const target = Math.min(1, Math.max(0, progress));
  let low = 0;
  let high = 1;

  for (let i = 0; i < 20; i++) {
    const mid = (low + high) / 2;
    const eased = (3 - 2 * mid) * mid * mid;
    if (eased < target) low = mid;
    else high = mid;
  }

  const t = (low + high) / 2;
  return (3 * (1 - t) * OPEN_EASE_X2 + t) * t * t;
}

class CircadianRhythmScroll extends HTMLElement {
  #activeIndex = 0;
  #scrollTrigger = null;
  #backgroundTween = null;
  #stateTimer;
  #suppressProgress = false;
  #onKeyDown = null;
  #keyStepTween = null;

  connectedCallback() {
    this.#placeClockProductMarkers();
    this.slides = Array.from(this.querySelectorAll('.circadian-rhythm-scroll__slide'));
    this.indexDots = Array.from(this.querySelectorAll('.circadian-rhythm-scroll__index-dot'));
    this.viewport = this.querySelector('.circadian-rhythm-scroll__viewport');
    this.liveRegion = this.querySelector('[data-period-live-region]');
    this.entranceContent = this.querySelector('.circadian-rhythm-scroll__entrance-content');
    this.morningContent = this.querySelector('.circadian-rhythm-scroll__morning-content');
    this.middayContent = this.querySelector('.circadian-rhythm-scroll__midday-content');
    this.afternoonContent = this.querySelector('.circadian-rhythm-scroll__afternoon-content');
    this.eveningContent = this.querySelector('.circadian-rhythm-scroll__evening-content');
    this.slideContents = [
      this.entranceContent,
      this.morningContent,
      this.middayContent,
      this.afternoonContent,
      this.eveningContent,
    ];
    if (!this.slides.length || !this.viewport) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#setupDotNavigation();
    if (this.slides.length <= 1) return;

    if (window.gsap && window.ScrollTrigger) {
      this.#setupScrollTrigger();
      return;
    }

    window.addEventListener('load', () => this.#setupScrollTrigger(), { once: true });
  }

  disconnectedCallback() {
    clearTimeout(this.#stateTimer);
    this.#keyStepTween?.kill();
    this.#backgroundTween?.kill();
    this.#scrollTrigger?.kill();
    if (this.#onKeyDown) window.removeEventListener('keydown', this.#onKeyDown);
  }

  #setupScrollTrigger() {
    if (this.#scrollTrigger || !window.gsap || !window.ScrollTrigger) return;

    window.gsap.registerPlugin(window.ScrollTrigger);

    const segments = this.slides.length - 1;

    this.#scrollTrigger = window.ScrollTrigger.create({
      trigger: this.viewport,
      pin: this.viewport,
      start: 'center center',
      end: `+=${segments * 100}%`,
      scrub: 0.5,
      snap: {
        snapTo: 1 / segments,
        duration: { min: 0.2, max: 0.4 },
        ease: 'power1.inOut',
      },
      onUpdate: (self) => this.#onProgress(self.progress, segments),
    });

    this.#onKeyDown = (event) => {
      const st = this.#scrollTrigger;
      const y = window.scrollY;
      // isActive is false at the exact start/end snap points; the pin band is inclusive.
      if (y < st.start || y > st.end) return;

      const down = event.key === 'ArrowDown' || event.key === 'PageDown';
      const up = event.key === 'ArrowUp' || event.key === 'PageUp';
      if (!down && !up) return;

      const currentIndex = Math.round(st.progress * segments);
      if ((down && currentIndex >= segments) || (up && currentIndex <= 0)) return;

      event.preventDefault();
      this.#stepTo(down ? 1 : -1, segments);
    };
    window.addEventListener('keydown', this.#onKeyDown);
  }

  #onProgress(progress, segments) {
    if (this.#suppressProgress) return;
    const index = Math.min(segments, Math.max(0, Math.round(progress * segments)));
    if (index === this.#activeIndex) return;
    this.#activateState(index);
  }

  #scrollTargetFor(index, segments) {
    const st = this.#scrollTrigger;
    return st.start + (index / segments) * (st.end - st.start);
  }

  #stepTo(direction, segments) {
    const st = this.#scrollTrigger;
    const currentIndex = Math.round(st.progress * segments);
    const nextIndex = Math.min(segments, Math.max(0, currentIndex + direction));
    const targetScroll = this.#scrollTargetFor(nextIndex, segments);

    this.#keyStepTween?.kill();
    const proxy = { y: window.scrollY };
    this.#keyStepTween = window.gsap.to(proxy, {
      y: targetScroll,
      duration: KEY_STEP_DURATION,
      ease: 'power2.out',
      onUpdate: () => window.scrollTo({ top: proxy.y, behavior: 'instant' }),
    });
  }

  #placeClockProductMarkers() {
    this.querySelectorAll('.circadian-rhythm-scroll__state').forEach((state) => {
      const arc = state.querySelector('.circadian-rhythm-scroll__arc');
      if (!arc || typeof arc.getTotalLength !== 'function') return;

      const pathLength = arc.getTotalLength();
      if (!pathLength) return;
      const labelGap = state.classList.contains('circadian-rhythm-scroll__state--24') ? 8 : LABEL_GAP;

      state.querySelectorAll('.circadian-rhythm-scroll__product-marker').forEach((marker) => {
        const slot = Math.min(12, Math.max(1, Number(marker.dataset.clockSlot) || 1));
        const slotProgress = (12 - slot) / 11;
        const revealProgress = 1 - slotProgress;
        const point = arc.getPointAtLength(slotProgress * pathLength);
        marker.style.setProperty(
          '--clock-reveal-delay',
          `${Math.round(OPEN_DELAY + timeAtOpenStrokeProgress(revealProgress) * OPEN_DURATION)}ms`
        );
        const circle = marker.querySelector('.circadian-rhythm-scroll__marker');
        const labelBox = marker.querySelector('.circadian-rhythm-scroll__label-box');
        const label = marker.querySelector('.circadian-rhythm-scroll__label');

        if (circle) {
          circle.setAttribute('cx', String(point.x));
          circle.setAttribute('cy', String(point.y));
        }

        const dx = point.x - DIAL_CX;
        const dy = point.y - DIAL_CY;
        const distance = Math.hypot(dx, dy) || 1;
        const ux = dx / distance;
        const uy = dy / distance;
        const labelY = point.y + uy * labelGap;
        const onRight = ux >= 0;
        const belowMarker = uy > 0;

        if (labelBox) {
          labelBox.setAttribute(
            'x',
            String(onRight ? point.x + labelGap : point.x - labelGap - LABEL_BOX_WIDTH)
          );
          labelBox.setAttribute(
            'y',
            String(belowMarker ? point.y + LOWER_LABEL_GAP : labelY - LABEL_BOX_HEIGHT / 2)
          );
          labelBox.setAttribute('width', String(LABEL_BOX_WIDTH));
          labelBox.setAttribute('height', String(LABEL_BOX_HEIGHT));
        }

        if (label) {
          label.style.textAlign = onRight ? 'left' : 'right';
          label.style.justifyContent = onRight ? 'flex-start' : 'flex-end';
          label.style.alignItems = belowMarker ? 'flex-start' : 'center';
        }
      });
    });
  }

  #setupDotNavigation() {
    this.indexDots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.slideIndex, 10);
        if (!isNaN(index)) this.#goToPeriod(index);
      });
    });
  }

  #goToPeriod(index) {
    if (index < 0 || index >= this.slides.length || index === this.#activeIndex) return;

    if (this.#scrollTrigger) {
      const segments = this.slides.length - 1;
      const targetScroll = this.#scrollTargetFor(index, segments);
      this.#keyStepTween?.kill();
      this.#activateState(index);
      this.#suppressProgress = true;
      window.scrollTo({ top: targetScroll, behavior: 'instant' });
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          this.#suppressProgress = false;
        })
      );
      return;
    }

    if (window.gsap) this.#activateState(index);
  }

  #updateIndex(index) {
    this.indexDots.forEach((dot, dotIndex) => {
      dot.classList.toggle('is-active', dotIndex === index);
    });
  }

  #announcePeriod(index) {
    if (!this.liveRegion) return;
    this.liveRegion.textContent = this.indexDots[index]?.getAttribute('aria-label') ?? '';
  }

  #activateState(index) {
    clearTimeout(this.#stateTimer);

    const outgoingSlide = this.slides[this.#activeIndex];
    const incomingSlide = this.slides[index];
    this.#activeIndex = index;
    this.dataset.activeState = STATES[index];
    this.classList.remove('is-closing');
    delete this.dataset.closingState;
    delete this.dataset.closeDirection;
    this.#updateIndex(index);
    this.#announcePeriod(index);

    this.#backgroundTween?.kill();
    this.#backgroundTween = window.gsap.timeline({ defaults: { duration: 0.45, ease: 'power2.inOut' } });
    this.#backgroundTween
      .to(outgoingSlide, { autoAlpha: 0 }, 0)
      .fromTo(incomingSlide, { autoAlpha: 0 }, { autoAlpha: 1 }, 0);

    this.slideContents.forEach((content, contentIndex) => {
      if (!content) return;

      if (index === contentIndex) {
        this.#backgroundTween.fromTo(content, { autoAlpha: 0 }, { autoAlpha: 1 }, contentIndex === 0 ? 0 : 0.2);
      } else {
        this.#backgroundTween.to(content, { autoAlpha: 0 }, 0);
      }
    });

    if (!this.dataset.activeState) {
      this.classList.remove('is-opening');
      return;
    }

    this.classList.remove('is-opening');
    void this.offsetWidth;
    this.classList.add('is-opening');
    this.#stateTimer = window.setTimeout(
      () => this.classList.remove('is-opening'),
      OPEN_DELAY + OPEN_DURATION + MARKER_FADE_DURATION
    );
  }
}

if (!customElements.get('circadian-rhythm-scroll')) {
  customElements.define('circadian-rhythm-scroll', CircadianRhythmScroll);
}
